import Head from 'next/head'
import styles from '@/styles/About.module.css'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import Pasta from '@/components/Pasta'

const ExtraSection = dynamic(() => import('@/components/ExtraSection'))

const pasta = () => {
  const { t } = useTranslation()
  const [itemListForJson, setItemListForJson] = useState([])

  useEffect(() => {
    const generateMissingImage = (width, height, alt) => {
      if (
        !width ||
        !height ||
        typeof width !== 'number' ||
        typeof height !== 'number'
      ) {
        return '' // or some default image URL
      }
      return (
        <Image
          src={`https://via.placeholder.com/${width}x${height}`}
          width={width}
          height={height}
          alt={alt || 'Image Not Available'}
          loading='lazy'
        />
      )
    }

    // Define recipe ingredients inside useEffect
    const homemadeLasagnaIngredients = [
      t('pasta sheets'),
      t('minced meat sauce'),
      t('cheese'),
      t('herbs'),
    ]

    const spagettiBologneseIngredients = [
      t('penne pasta'),
      t('minced meat sauce'),
      t('herbs'),
    ]

    const spagettiCarbonaraIngredients = [
      t('penne pasta'),
      t('bacon'),
      t('cooking cream sauce'),
      t('herbs'),
    ]

    const chickenCurryIngredients = [
      t('penne pasta'),
      t('chicken'),
      t('curry sauce'),
      t('herbs'),
    ]

    const beefPastaIngredients = [
      t('penne pasta'),
      t('beef fillet'),
      t('creamy sauce'),
      t('herbs'),
    ]

    // Set itemListForJson inside useEffect
    const itemListElement = [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Recipe',
          name: t('Homemade Lasagna'),
          image: generateMissingImage(400, 300),
          description: t(
            'Enjoy the rich flavors of our homemade lasagna, crafted with layers of pasta and savory minced meat sauce. Each bite is a delightful blend of textures and aromas, guaranteed to satisfy your taste buds.'
          ),
          recipeIngredient: homemadeLasagnaIngredients,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Recipe',
          name: t('Spagetti Bolognese'),
          image: '/pastaBolognese.webp',
          description: t(
            `Indulge in our classic Spagetti Bolognese, featuring al dente penne pasta smothered in rich and hearty minced meat sauce. It's a timeless Italian favorite that never disappoints.`
          ),
          recipeIngredient: spagettiBologneseIngredients,
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Recipe',
          name: t('Spagetti Carbonara'),
          image: '/carbonara.webp',
          description: t(
            'Treat yourself to the creamy goodness of Spagetti Carbonara. Made with tender penne pasta, crispy bacon, and a rich cooking cream sauce, every bite is a burst of indulgent flavor.'
          ),
          recipeIngredient: spagettiCarbonaraIngredients,
        },
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Recipe',
          name: t('Chicken Curry'),
          image: '/pastaKyckling.webp',
          description: t(
            `Savor the exotic flavors of our Kyckling Curry pasta. Tender penne pasta combined with succulent chicken and a fragrant curry sauce create a dish that's both comforting and exciting.`
          ),
          recipeIngredient: chickenCurryIngredients,
        },
      },
      {
        '@type': 'ListItem',
        position: 5,
        item: {
          '@type': 'Recipe',
          name: t('Beef Pasta'),
          image: '/oxfiléPasta.webp',
          description: t(
            `Treat yourself to the luxurious flavors of our Oxfilé pasta. Featuring tender penne pasta and succulent beef fillet in a creamy sauce, it's a culinary delight that's sure to impress.`
          ),
          recipeIngredient: beefPastaIngredients,
        },
      },
    ]

    setItemListForJson(itemListElement)
  }, [t])

  return (
    <main className={styles.main}>
      <Head>
        <title>La Bambi Restaurang - Pasta Meny</title>
        <meta
          name='description'
          content='Njut av de rika smakerna av [oxfilé pasta], ett kulinariskt mästerverk med [Oxfilé, champinjoner och grädde]. Varje tugga är en symfoni av texturer och aromer, från al dente-pastan till den krämiga såsen och de salta påläggen. Den här rätten kommer säkerligen att reta dina smaklökar och göra dig sugen på mer.'
        />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        {/* Schema.org structured data */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: itemListForJson,
            }),
          }}
        />
      </Head>
      <Pasta />
    </main>
  )
}

export default pasta
